/**
 * Bộ dựng chung cho 15 màn hình — giữ đúng hình khối và màu của `AGADS.dc.html`.
 *
 * Mọi màn dùng lại bộ này thay vì tự viết style, để khi BOSS muốn đổi giao diện
 * thì sửa một chỗ.
 */

import { useEffect, useId, useState } from 'react';
import type { CSSProperties, InputHTMLAttributes, ReactNode } from 'react';

export function The({
  children,
  style,
  ton,
}: {
  children: ReactNode;
  style?: CSSProperties;
  /** Sắc thái nền: mặc định trắng, hoặc tốt / cảnh báo / xấu. */
  ton?: 'tot' | 'canh-bao' | 'xau';
}) {
  const mau =
    ton === 'tot'
      ? { background: 'var(--agads-xanh-la-nen)', borderColor: 'var(--agads-xanh-la-vien)', color: 'var(--agads-xanh-la-chu)' }
      : ton === 'canh-bao'
        ? { background: 'var(--agads-vang-nen)', borderColor: 'var(--agads-vang-vien)', color: 'var(--agads-vang-chu-dam)' }
        : ton === 'xau'
          ? { background: 'var(--agads-do-nen)', borderColor: 'var(--agads-do-vien)', color: 'var(--agads-do-chu-dam)' }
          : {};
  return (
    <section className="ag-the" style={{ ...mau, ...style }}>
      {children}
    </section>
  );
}

export function TieuDe({ children, phu }: { children: ReactNode; phu?: ReactNode }) {
  return (
    <>
      <h2 style={{ fontSize: 15 }}>{children}</h2>
      {phu && (
        <p className="ag-mo" style={{ marginTop: 4 }}>
          {phu}
        </p>
      )}
    </>
  );
}

const TON_HUY_HIEU: Record<string, CSSProperties> = {
  tot: { background: 'var(--agads-xanh-la-nen)', color: 'var(--agads-xanh-la-dam)' },
  'canh-bao': { background: 'var(--agads-vang-nen)', color: 'var(--agads-vang-chu)' },
  xau: { background: 'var(--agads-do-nen)', color: 'var(--agads-do-chu)' },
  tin: { background: 'var(--agads-info-nen)', color: 'var(--agads-info-chu)' },
  tim: { background: 'var(--agads-tim-nen)', color: 'var(--agads-tim-chu)' },
  cam: { background: 'var(--agads-nen-xam)', color: 'var(--agads-muc-nhat)' },
};

export function HuyHieu({
  children,
  ton = 'cam',
}: {
  children: ReactNode;
  ton?: keyof typeof TON_HUY_HIEU | string;
}) {
  return (
    <span className="ag-huy-hieu" style={TON_HUY_HIEU[ton] ?? TON_HUY_HIEU.cam}>
      {children}
    </span>
  );
}

/** Phù hiệu mức hành động — dùng đúng 4 màu của thiết kế. */
export function HuyHieuTier({ tier }: { tier: string }) {
  return <span className={`ag-huy-hieu ag-tier ag-tier--${tier}`}>{tier}</span>;
}

export function Chip({
  children,
  dangChon,
  onClick,
}: {
  children: ReactNode;
  dangChon?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '8px 14px',
        borderRadius: 'var(--agads-bo-goc-tron)',
        fontSize: 12.5,
        cursor: 'pointer',
        fontFamily: 'inherit',
        background: dangChon ? 'var(--agads-primary)' : 'var(--agads-nen-trang)',
        border: `1px solid ${dangChon ? 'var(--agads-primary)' : 'var(--agads-vien-2)'}`,
        color: dangChon ? '#fff' : 'var(--agads-muc-2)',
        fontWeight: dangChon ? 600 : 400,
      }}
    >
      {children}
    </button>
  );
}

export function HangChip({ children }: { children: ReactNode }) {
  return <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>{children}</div>;
}

export function Nut({
  children,
  onClick,
  chinh,
  nguyHiem,
  tat,
  style,
  type = 'button',
}: {
  children: ReactNode;
  onClick?: () => void;
  chinh?: boolean;
  nguyHiem?: boolean;
  tat?: boolean;
  style?: CSSProperties;
  type?: 'button' | 'submit';
}) {
  const nen = tat
    ? 'var(--agads-vien-tat)'
    : chinh
      ? 'var(--agads-primary)'
      : 'var(--agads-nen-trang)';
  const chu = tat
    ? 'var(--agads-muc-tat)'
    : chinh
      ? '#fff'
      : nguyHiem
        ? 'var(--agads-do)'
        : 'var(--agads-muc-2)';
  return (
    <button
      type={type}
      disabled={tat}
      onClick={onClick}
      style={{
        borderRadius: 10,
        padding: '11px 17px',
        fontSize: 13.5,
        fontWeight: chinh ? 600 : 500,
        cursor: tat ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit',
        background: nen,
        border: chinh || tat ? 'none' : '1px solid var(--agads-vien-3)',
        color: chu,
        ...style,
      }}
    >
      {children}
    </button>
  );
}

/** Thanh tiến độ ngang — dùng cho trần chi, phễu, so sánh kênh. */
export function Thanh({ phanTram, mau }: { phanTram: number; mau?: string }) {
  return (
    <div
      style={{
        height: 8,
        borderRadius: 5,
        background: 'var(--agads-nen-xam)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${Math.max(0, Math.min(phanTram, 100))}%`,
          borderRadius: 5,
          background: mau ?? 'var(--agads-primary)',
        }}
      />
    </div>
  );
}

/** Bảng cuộn ngang trên điện thoại — thiết kế gốc dùng bảng cho máy tính. */
export function Bang({ cot, children }: { cot: ReactNode[]; children: ReactNode }) {
  return (
    <div style={{ overflowX: 'auto', marginTop: 12 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 520 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--agads-muc-nhat)' }}>
            {cot.map((c, i) => (
              <th key={typeof c === 'string' ? c || i : i} style={{ padding: '8px 10px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function O({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <td style={{ padding: '10px', ...style }}>{children}</td>;
}

export function Dong({ children }: { children: ReactNode }) {
  return <tr style={{ borderTop: '1px solid var(--agads-vien)' }}>{children}</tr>;
}

/** Trạng thái tải / lỗi dùng chung, để mọi màn báo lỗi giống nhau. */
export function TrangThaiTai({
  dangTai,
  loi,
  children,
}: {
  dangTai: boolean;
  loi: unknown;
  children: ReactNode;
}) {
  if (dangTai) return <p className="ag-mo">Đang tải…</p>;
  if (loi) {
    const tb = loi instanceof Error ? loi.message : String(loi);
    const status = (loi as { status?: number }).status;
    return (
      <The ton={status === 403 ? 'canh-bao' : 'xau'}>
        <TieuDe>{status === 403 ? 'Không có quyền' : 'Không tải được dữ liệu'}</TieuDe>
        <p style={{ marginTop: 6, fontSize: 13 }}>{tb}</p>
      </The>
    );
  }
  return <>{children}</>;
}

export const oNhap: CSSProperties = {
  border: '1px solid var(--agads-vien-2)',
  borderRadius: 'var(--agads-bo-goc-nho)',
  padding: '10px 12px',
  fontSize: 14,
  fontFamily: 'inherit',
  background: 'var(--agads-nen-trang)',
  width: '100%',
};

/**
 * Ô nhập mật khẩu — LUÔN có nút con mắt để hiện/ẩn ký tự.
 *
 * BOSS đặt thành nguyên tắc chung (17/09/2026): mọi ô mật khẩu trong AGADS đều
 * phải cho người dùng xem lại mình vừa gõ gì. Gõ mù một chuỗi dài rồi bị báo
 * "mật khẩu không đúng" mà không biết sai ở đâu là rất ức chế — nhất là khi đổi
 * mật khẩu, phải gõ đúng ba ô liền.
 *
 * Vì vậy KHÔNG dùng `<input type="password">` trực tiếp ở bất kỳ màn nào nữa —
 * dùng component này, để nút con mắt không thể bị quên.
 *
 * Mặc định vẫn là ẩn: hiện chỉ khi người dùng chủ động bấm, và mỗi ô tự giữ
 * trạng thái riêng nên bấm ô này không lộ ô kia.
 */
export function OMatKhau({
  style,
  ...conLai
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  const [hien, setHien] = useState(false);
  const maO = useId();
  const { khung, oNhapRieng } = tachStyleONhap(style);

  return (
    <div style={{ position: 'relative', display: 'flex', width: '100%', ...khung }}>
      <input
        {...conLai}
        id={conLai.id ?? maO}
        type={hien ? 'text' : 'password'}
        // Chừa chỗ cho nút con mắt, để ký tự gõ dài không chui xuống dưới nút.
        style={{ ...oNhap, paddingRight: 42, ...oNhapRieng, width: '100%' }}
      />
      <button
        type="button"
        onClick={() => setHien((v) => !v)}
        aria-label={hien ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
        aria-pressed={hien}
        title={hien ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
        // Không nhận tiêu điểm bằng phím Tab: người dùng bàn phím gõ xong là
        // sang thẳng ô kế tiếp, không phải bước qua một nút phụ trợ.
        tabIndex={-1}
        style={{
          position: 'absolute',
          right: 1,
          top: 1,
          bottom: 1,
          width: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          background: 'transparent',
          color: hien ? 'var(--agads-primary)' : 'var(--agads-muc-2)',
          cursor: 'pointer',
          borderRadius: 'var(--agads-bo-goc-nho)',
        }}
      >
        <MatNhin mo={hien} />
      </button>
    </div>
  );
}

/**
 * Chia `style` gọi vào làm hai: phần quyết định BỀ RỘNG đưa cho khung bọc, phần
 * còn lại (viền, nền, cỡ chữ…) đưa cho ô nhập.
 *
 * Cần thiết vì nút con mắt đặt tuyệt đối theo khung bọc. Nếu khung rộng 100% mà
 * ô nhập lại `maxWidth: 320` thì con mắt trôi ra tận mép phải, cách ô một quãng
 * trống — đúng lỗi đã thấy khi dựng xong màn Hồ sơ.
 */
function tachStyleONhap(style?: CSSProperties): {
  khung: CSSProperties;
  oNhapRieng: CSSProperties;
} {
  if (!style) return { khung: {}, oNhapRieng: {} };
  const { width, maxWidth, minWidth, flex, ...oNhapRieng } = style;
  return { khung: { width, maxWidth, minWidth, flex }, oNhapRieng };
}

/** Con mắt mở / con mắt gạch chéo. */
function MatNhin({ mo }: { mo: boolean }) {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
      {!mo && <path d="M3 3l18 18" />}
    </svg>
  );
}

/** Công tắc bật/tắt — hình dáng lấy từ thiết kế gốc. */
export function CongTac({
  bat,
  onClick,
  tat,
}: {
  bat: boolean;
  onClick: () => void;
  tat?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={bat}
      style={{
        flex: 'none',
        width: 46,
        height: 27,
        borderRadius: 14,
        border: 'none',
        cursor: tat ? 'not-allowed' : 'pointer',
        padding: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: bat ? 'flex-end' : 'flex-start',
        background: tat
          ? 'var(--agads-vien-4)'
          : bat
            ? 'var(--agads-primary)'
            : 'var(--agads-vien-4)',
        opacity: tat ? 0.6 : 1,
      }}
    >
      <span
        style={{
          display: 'block',
          width: 21,
          height: 21,
          borderRadius: 11,
          background: '#fff',
          boxShadow: '0 1px 2px rgba(26,46,41,.2)',
        }}
      />
    </button>
  );
}

/**
 * Hộp thoại giữa màn hình — dùng cho chi tiết, sửa key, thêm mới.
 *
 * Bấm nền hoặc phím Esc là đóng, và khoá cuộn nền trong lúc mở để không bị
 * cuộn nhầm trang phía sau.
 */
export function HopThoai({
  tieuDe,
  dong,
  children,
  rong = 480,
}: {
  tieuDe: string;
  dong: () => void;
  children: ReactNode;
  rong?: number;
}) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const nghe = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dong();
    };
    window.addEventListener('keydown', nghe);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', nghe);
    };
  }, [dong]);

  return (
    <>
      <button
        type="button"
        className="ag-ngan-keo-nen"
        aria-label="Đóng"
        onClick={dong}
        style={{ zIndex: 60 }}
      />
      <div
        role="dialog"
        aria-label={tieuDe}
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 70,
          width: `min(${rong}px, calc(100vw - 32px))`,
          maxHeight: '86vh',
          overflowY: 'auto',
          background: 'var(--agads-nen-trang)',
          borderRadius: 'var(--agads-bo-goc)',
          padding: '18px 20px',
          boxShadow: '0 12px 40px rgba(26,46,41,.25)',
        }}
      >
        <h2 style={{ fontSize: 16, marginBottom: 12 }}>{tieuDe}</h2>
        {children}
      </div>
    </>
  );
}
